import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ISBT_FIELDS, formatIsbtValue, type IsbtField } from '@/lib/isbtCatalog'
import type { ProductCatalogEntry } from '@/types/products'

/**
 * One ISBT 128 product code in full — what the Product Code link opens.
 *
 * The raw HL7 description sits at the top because it is the record: the 40
 * parsed columns below are derived from it and were not correct on every row,
 * so a reader checking a surprising value needs the original in the same view,
 * not a tab away.
 *
 * Blank columns are shown rather than hidden. '' means the standard does not
 * state that attribute for this code, which is information; dropping the row
 * would leave a reader unable to tell "not stated" from "we didn't parse it".
 */

const GROUP_LABELS: Record<IsbtField['group'], string> = {
  identity: 'Identity',
  product: 'Product',
  triplet: 'Anticoagulant / Volume / Temperature',
  attribute: 'Attributes',
  source: 'Source',
}

// `source` is rendered at the top as the HL7 string, not repeated in the grid.
const GRID_GROUPS: IsbtField['group'][] = ['identity', 'product', 'triplet', 'attribute']

export function ProductCodeDialog({
  entry,
  onClose,
}: {
  entry: ProductCatalogEntry | null
  onClose: () => void
}) {
  return (
    <Dialog open={!!entry} onOpenChange={next => !next && onClose()}>
      <DialogContent className='max-h-[85vh] overflow-y-auto sm:max-w-3xl'>
        {entry && (
          <>
            <DialogHeader>
              <DialogTitle className='flex items-center gap-2'>
                <span className='font-mono'>{entry.productDescriptionCode}</span>
                {entry.expired && (
                  <span className='rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-800'>
                    Expired
                  </span>
                )}
              </DialogTitle>
              <DialogDescription>
                ISBT 128 product code — {entry.attributeCount} attribute
                {entry.attributeCount === 1 ? '' : 's'} in the original description.
              </DialogDescription>
            </DialogHeader>

            <div className='space-y-1'>
              <p className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
                {GROUP_LABELS.source} — the original HL7 string
              </p>
              {/* `break-all` rather than a scroller: these run to 195 characters
                  and are read for their pipe-separated parts, so wrapping the
                  whole string beats making the reader scroll it sideways. */}
              <p className='rounded-lg border bg-muted/40 px-3 py-2 font-mono text-xs leading-relaxed break-all'>
                {entry.description || '—'}
              </p>
            </div>

            {GRID_GROUPS.map(group => {
              const fields = ISBT_FIELDS.filter(f => f.group === group)
              return (
                <div key={group} className='space-y-2'>
                  <p className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
                    {GROUP_LABELS[group]}
                  </p>
                  <dl className='grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2'>
                    {fields.map(field => (
                      <div
                        key={field.key}
                        className='flex items-baseline justify-between gap-3 border-b border-dashed border-border/60 pb-1'
                      >
                        <dt className='shrink-0 text-xs text-muted-foreground'>{field.label}</dt>
                        <dd className='text-right text-xs font-medium'>
                          {formatIsbtValue(entry, field.key)}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )
            })}
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
